import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { sendUserNewsletterOptInMail } from '@adornis/buchungsheld-db/api/users.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/forms/x-checkbox.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { filter, map, of, startWith, switchMap } from 'rxjs';
import { BHPromptPhoneNumber } from '../../components/bh-prompt-phone-number.js';
import { BHRegistrationFlowSubPage } from './bh-page-registration-sub-page.js';

// Define type for user preference properties for better type safety
type UserPreferenceProperty = 'receivesNewsletter' | 'whatsappNotification';

@customElement('bh-page-registration-terms')
export class BHPageRegistrationTerms extends BHRegistrationFlowSubPage {
  override styles() {
    return [
      {
        'ol li': {
          marginBottom: this.spacing.sm,
        },
      },
    ];
  }

  @state() private readonly _registrationData = new RXController(
    this,
    this._registrationController$.pipe(switchMap(regController => regController?.registrationData$ ?? of(null))),
  );

  @state() private readonly userFormController = new RXController(
    this,
    currentUser<User>().pipe(
      startWith(new User({})),
      filter(Boolean),
      map(user => new FormController(this, new User({}))),
    ),
  );

  override backAction = () => this._registrationController.previous();
  override nextButtonText = ['REGISTRATION', 'book_now'];

  override nextAction = async () => {
    const user = this._registrationController.currentUser;
    const receivesNewsletter = this.userFormController.value?.document?.receivesNewsletter ?? false;
    const whatsappNotification = this.userFormController.value?.document?.whatsappNotification ?? false;
    if (!user) throw new Error('User not found when trying to update preferences');

    if (receivesNewsletter) await sendUserNewsletterOptInMail(user._id)();

    if (whatsappNotification) {
      user!.whatsappNotification = true;
      const phonenumber = (await BHPromptPhoneNumber.showPopup({
        props: { user: this._registrationController.currentUser },
      })) as Maybe<string>;

      if (phonenumber) {
        user.whatsappPhone = phonenumber;
        await user.save();
      }
    }

    await this.handleUserPreferenceChange('whatsappNotification', whatsappNotification);
    await this._registrationController.next();
  };

  private async handleUserPreferenceChange(property: UserPreferenceProperty, value: boolean) {
    const user = this._registrationController.currentUser;
    if (!user) {
      this.e(new Error('User not found when trying to update preferences'));
      return;
    }

    user[property] = value;
    await XDialog.waitFor(user.save()).catch(this.e);
  }

  override content() {
    if (!this._registrationController.institution) return html`<x-loading-state></x-loading-state>`;
    const currentUser = this._registrationController.currentUser;

    this.nextButtonDisabled = !this._registrationData.value?.acceptedTerms;

    return html`
      <x-h3>${this._translation.translate('termsOfUse')}</x-h3>
      <tr-text> ${unsafeHTML(this._registrationController.institution.resolvedTermsOfUse)} </tr-text>

      <!-- Accept terms of use -->

      <x-checkbox
        .label=${this._translation.translate('REGISTRATION', 'read_terms_of_use')}
        @value-picked=${(e: ValueEvent<boolean>) => this._registrationController.acceptTerms(e.detail.value)}
        .value=${this._registrationData.value?.acceptedTerms ?? false}
      ></x-checkbox>

      <!-- Want to receive Newsletter -->
      ${currentUser && !currentUser.receivesNewsletter
        ? html`
            <x-checkbox
              .label=${this._translation.translate('REGISTRATION', 'i_want_to_receive_newsletter')}
              ${this.userFormController.value?.field('receivesNewsletter')}
            ></x-checkbox>
          `
        : nothing}

      <!-- Want to receive WhatsApp notifications -->
      ${currentUser && !currentUser.whatsappNotification && !!this._registrationController.institution?.whatsappEnabled
        ? html`
            <x-checkbox
              .label=${this._translation.translate('REGISTRATION', 'i_want_to_receive_whatsapp_notifications')}
              ${this.userFormController.value?.field('whatsappNotification')}
            ></x-checkbox>
          `
        : nothing}
    `;
  }
}
